import { Injectable } from "@angular/core";
import { ContentPipe } from "../services/content/content.pipe";
import { RandomUUIDPipe } from "../shared/pipes/random-uuid.pipe";
import { AssessmentQuestionTypeEnum } from "./models/assessments/assessment.enum";
import { AssessmentsMineOSFormControl } from "../ai-assessments/models/assessments.interface";
import { AssessmentsTemplate, AssessmentsTemplateRequest, AssessmentsTemplatesRequest, AssessmentsTemplatesResponse } from "./models/assessments/assessment-template.interface";
import { DropdownOption } from "../shared/mine-dropdown/mine-dropdown.interface";
import { CustomFieldsV2Service } from "../company-settings/custom-fields-v2/custom-fields-v2.service";
import { CustomFieldBehaviorEnum, CustomFieldInputTypeEnum } from "./models/company-settings/custom-field-v2.enum";
import { CustomFieldOptionV2, CustomFieldV2Response } from "./models/company-settings/custom-field-v2.interface";

import * as he from 'he';
import { AssessmentsLogicManagerService } from "../ai-assessments/services/assessments-logic-manager.service";

@Injectable({
	providedIn: 'root'
})
export class AssessmentsTemplateParserService {

    constructor(
        private contentPipe: ContentPipe,
        private randomUUIDPipe: RandomUUIDPipe,
        private assessmentsLogicManagerService: AssessmentsLogicManagerService,
        private customFieldsV2Service: CustomFieldsV2Service,
    ) {}

    decodeTemplateSchema(template: AssessmentsTemplate): AssessmentsMineOSFormControl[] {
        if (!!template.schema) {
            const decodedSchema = he.decode(template.schema.replace(/&quot;/g,"''"));
            let schema = JSON.parse(decodedSchema);

            return this.processTemplateSchema(schema);
        }
        return [];
    }

    processTemplateSchema(schema: any[]): AssessmentsMineOSFormControl[] {
        if (schema) {
            schema = schema.map(section => this.parseSectionControls(section));
            schema = this.updateSectionsWithCustomFields(schema);
        }
        return schema ?? [];
    }

    parseTemplateRequestToPageV2(request: AssessmentsTemplateRequest): AssessmentsTemplatesRequest {
        const sections = [];
        const attachmentsBySectionIndex = request.attachments ?? [];
        const { name, type, dataSourceSpecific, tagIds, logic } = request;

        let requestSchema;

        try {
            requestSchema = JSON.parse(request.schema);
        } catch (err) {
            console.error('Could not parse request schema', err);
            throw new Error(this.contentPipe.transform('ai-assessments.parsingDataErrorMessage'));
        }

        requestSchema.forEach((section, index) => {
            if (!section.type) {
                section.type = AssessmentQuestionTypeEnum.Group;
            }
            const {name, label, type, isHiddenFromCollaborators} = section;
            const ordinal = index;
            const data = JSON.stringify({});
            const schema = JSON.stringify(section.controls?.length ? section.controls : []);
            const attachments = attachmentsBySectionIndex[index] ?? [];
            sections.push({name, label, type, ordinal, schema, data, attachments, isHiddenFromCollaborators: !!isHiddenFromCollaborators});
        });

        return { name, sections, type, dataSourceSpecific, logic, tagIds } as AssessmentsTemplatesRequest;
    }

    parseTemplatePageToResponse(templatePage: AssessmentsTemplatesResponse): AssessmentsTemplate {    
        const templateResponse = {
            id: templatePage.id.toString(),
            name: templatePage.name,
            agentId: templatePage.agentId,
            type: templatePage.type,
            createdDate: templatePage.createdDate,
            lastModified: templatePage.updatedDate,
            dataSourceSpecific: templatePage.dataSourceSpecific,
            tags: templatePage.tags,
            tagIds: templatePage.tagIds
        } as AssessmentsTemplate;

        const schema: AssessmentsMineOSFormControl[] = [];
        templatePage.sections
            ?.sort((a, b) => a.ordinal - b.ordinal)
            ?.forEach(section => {
                let controls = JSON.parse(section.schema);
                if (typeof controls === "string") {
                    controls = JSON.parse(controls);
                }
                if (this.isObjectWithKey(controls, 'controls')) {
                    controls = controls.controls;
                }
                schema.push({
                    name: section.name,
                    label: section.label,
                    controls: controls,
                    type: section.type,
                    // preserve the server's optional boolean as-is; the write path coerces with !!
                    isHiddenFromCollaborators: section.isHiddenFromCollaborators
                } as AssessmentsMineOSFormControl);
        });

        if (templatePage.logic) {
            templateResponse.logic = templatePage.logic;
        }

        schema.forEach(section => {
            section.id = section.id ?? this.assessmentsLogicManagerService.generateSectionUuid(section);
            section.controls?.forEach(control => control.id = control.id ?? this.assessmentsLogicManagerService.generateControlUuid(control));
        });

        return {...templateResponse, schema: JSON.stringify(schema)} as AssessmentsTemplate;
    }

    private isObjectWithKey(variable: any, key: string): boolean {
        return typeof variable === 'object' && variable !== null && variable.hasOwnProperty(key);
    }

    private parseSectionControls(section: AssessmentsMineOSFormControl): AssessmentsMineOSFormControl {
        let controls = section.controls;
        if (typeof controls === 'string') {
          controls = JSON.parse(controls);
        }

        const controlsWithIds = (controls ?? []).map(control => ({
            ...control,
            id: control.id ?? this.assessmentsLogicManagerService.generateControlUuid(control),
        }));
        
        const response = {...section, controls: controlsWithIds } as AssessmentsMineOSFormControl;
        response.id = this.assessmentsLogicManagerService.generateSectionUuid(response);
        return response;
    }

    private updateSectionsWithCustomFields(sections: AssessmentsMineOSFormControl[]): AssessmentsMineOSFormControl[] {
        const updatedSections: AssessmentsMineOSFormControl[] = [];
        sections.forEach(section => {
            const updatedSection: AssessmentsMineOSFormControl = { ...section, controls: [] };
            for (let control of section.controls) {
                if (control.customFieldId) {
                    const v2Field = this.customFieldsV2Service.getField(control.customFieldId);
                    if (v2Field) {
                        this.alignControlWithCustomFieldV2(control, v2Field);
                    }
                    else {
                        continue;
                    }
                }
                updatedSection.controls.push(control);
            }
            updatedSections.push(updatedSection);
        });
        return updatedSections;
    }

    private alignControlWithCustomFieldV2(control: AssessmentsMineOSFormControl, customField: CustomFieldV2Response): void {
        control.type = this.getCustomFieldControlTypeV2(customField);
        control.label = customField.name;
        const behavior = this.parseV2Layout(customField.layout);
        control.multiple = !!behavior[CustomFieldBehaviorEnum.MultipleChoice];
        control.options = this.getOptionsForNewDropdownControlV2(customField.options);
    }

    private getCustomFieldControlTypeV2(customField: CustomFieldV2Response): AssessmentQuestionTypeEnum {
        switch (customField.inputType) {
            case CustomFieldInputTypeEnum.Text:
                return AssessmentQuestionTypeEnum.Text;
            case CustomFieldInputTypeEnum.Select:
                return AssessmentQuestionTypeEnum.Dropdown;
        }
    }

    private getOptionsForNewDropdownControlV2(options?: CustomFieldOptionV2[]): DropdownOption[] {
        const defaultOption = [{ id: `o-${this.randomUUIDPipe.transform()}`, value: null } as DropdownOption];
        return (options?.length > 0 ? options.map(o => ({ id: o.id, value: o.text, chipColor: {backgroundColor: o.color, textColor: '--mine-blue-dark'}} as DropdownOption)) : defaultOption);
    }

    private parseV2Layout(layout?: string): Record<string, boolean> {
        if (!layout) return {};
        try {
            return JSON.parse(layout) as Record<string, boolean>;
        } catch {
            return {};
        }
    }
}